import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { api, ApiError } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { formatDollars } from "@/lib/utils";

// ─── TAX VISIBILITY + PER-DOCUMENT TOGGLE ──────────────────────────────────
//
// Tax transparency for the invoice/estimate editor. The server owns the math
// (taxEngine.ts); this only surfaces the treatment and lets the office resolve
// it. Three states, always visible:
//   • taxable → "Sales tax (8.25%): $X"
//   • exempt  → "Tax — exempt" + the exempt reason
//   • unknown → an amber "Taxable or exempt?" prompt with one-click set,
//               reusing the send-dialog pattern (PATCH the CUSTOMER, which
//               recomputes every draft for them).
// A per-document override (draft-only, reason required) can deviate a single
// doc from the customer default; issued docs are frozen read-only.

export interface TaxDocFields {
    tax_amount?: string | null;
    tax_rate_pct?: string | null;
    tax_status_override?: string | null;
    tax_override_reason?: string | null;
    customer_tax_status?: string | null;
    customer_tax_exempt_reason?: string | null;
}

/** Mirrors the server's documentTaxPresentation — the tax line is ALWAYS
 *  present and self-explaining, never blank. */
export function taxLineFor(doc: TaxDocFields): { label: string; amount: string; note: string | null } {
    const effective = doc.tax_status_override ?? doc.customer_tax_status ?? "unknown";
    if (effective === "exempt") {
        const reason = (doc.tax_override_reason ?? doc.customer_tax_exempt_reason ?? "").trim();
        return { label: "Tax — exempt", amount: "—", note: reason ? `Exempt: ${reason}` : null };
    }
    if (effective === "unknown") return { label: "Tax — status not set", amount: "—", note: null };
    const rate = doc.tax_rate_pct != null ? Number(doc.tax_rate_pct) : null;
    const label =
        rate != null && Number.isFinite(rate) ? `Sales tax (${rate % 1 === 0 ? rate.toFixed(0) : rate}%)` : "Tax";
    return { label, amount: formatDollars(Number(doc.tax_amount ?? 0)), note: null };
}

function apiErrorMessage(e: unknown, fallback: string): string {
    if (e instanceof ApiError && e.body && typeof e.body === "object" && "message" in e.body) {
        return String((e.body as { message?: unknown }).message ?? "") || fallback;
    }
    return fallback;
}

export function TaxTreatmentPanel({
    docType,
    docId,
    customerId,
    doc,
    editable,
    onSaved,
}: {
    docType: "estimate" | "invoice";
    docId: string;
    customerId: string | null;
    doc: TaxDocFields;
    /** True only while the doc is a draft — issued docs are frozen. */
    editable: boolean;
    onSaved: () => void;
}) {
    const [editing, setEditing] = useState(false);
    const [choice, setChoice] = useState<string>(doc.tax_status_override ?? "inherit");
    const [reason, setReason] = useState(doc.tax_override_reason ?? "");

    const line = taxLineFor(doc);
    const overridden = !!doc.tax_status_override;
    const customerStatus = doc.customer_tax_status ?? "unknown";
    const effective = doc.tax_status_override ?? doc.customer_tax_status ?? "unknown";
    const source = overridden
        ? `Overridden for this ${docType}${doc.tax_override_reason ? ` — ${doc.tax_override_reason}` : ""}`
        : `From customer record${
              customerStatus === "exempt" && doc.customer_tax_exempt_reason
                  ? ` (${doc.customer_tax_exempt_reason})`
                  : ""
          }`;

    // Per-document override (deviate one doc from the customer default).
    const save = useMutation({
        mutationFn: () =>
            api.patch(`/api/${docType === "estimate" ? "estimates" : "invoices"}/${docId}`, {
                taxStatusOverride: choice === "inherit" ? null : choice,
                taxOverrideReason: choice === "inherit" ? null : reason.trim(),
            }),
        onSuccess: () => {
            toast.success("Tax treatment updated");
            setEditing(false);
            onSaved();
        },
        onError: (e: unknown) => toast.error(apiErrorMessage(e, "Could not update tax treatment.")),
    });

    // One-click unknown → set the CUSTOMER's tax status (reuses the send-dialog
    // pattern). The server recomputes this customer's draft docs on write.
    const setCustomerTax = useMutation({
        mutationFn: (status: "taxable" | "exempt") => api.patch(`/api/customers/${customerId}`, { taxStatus: status }),
        onSuccess: (_data, status) => {
            toast.success(
                status === "taxable" ? "Customer marked taxable — tax applied" : "Customer marked tax exempt",
            );
            onSaved();
        },
        onError: (e: unknown) => toast.error(apiErrorMessage(e, "Could not update the customer's tax status.")),
    });
    const resolving = setCustomerTax.isPending ? (setCustomerTax.variables ?? null) : null;

    const showUnknownPrompt = editable && effective === "unknown" && !!customerId;

    return (
        <div className="mt-2 space-y-1.5 border-t pt-2">
            {/* Always-visible tax line. */}
            <div className="flex items-center justify-between gap-2 text-sm" title={line.note ?? undefined}>
                <span className="flex items-center gap-1.5 text-foreground/80">
                    {line.label}
                    {overridden && (
                        <Badge
                            variant="outline"
                            className="border-amber-500/60 px-1.5 py-0 text-[10px] font-medium text-amber-700 dark:text-amber-300"
                        >
                            Override
                        </Badge>
                    )}
                </span>
                <span className="font-medium">{line.amount}</span>
            </div>
            {line.note && <p className="text-xs text-foreground/60">{line.note}</p>}
            <p className="text-xs text-foreground/60">{source}</p>

            {/* Unknown → amber one-click prompt (sets the customer). */}
            {showUnknownPrompt && (
                <div className="space-y-2 rounded-md border border-amber-400/60 bg-amber-50 p-3 dark:border-amber-500/50 dark:bg-amber-950/40">
                    <p className="flex items-start gap-2 text-sm font-medium text-amber-900 dark:text-amber-200">
                        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                        Taxable or exempt? Tax status isn&apos;t set for this customer.
                    </p>
                    <p className="text-xs text-amber-800/90 dark:text-amber-300/90">
                        Picking one updates the customer and recalculates every draft {docType} for them.
                    </p>
                    <div className="flex flex-wrap gap-2 pt-1">
                        <Button
                            type="button"
                            size="sm"
                            className="h-8 bg-amber-700 text-white hover:bg-amber-800"
                            disabled={setCustomerTax.isPending}
                            onClick={() => setCustomerTax.mutate("taxable")}
                        >
                            {resolving === "taxable" && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
                            Taxable — apply tax
                        </Button>
                        <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="h-8 border-amber-500/60"
                            disabled={setCustomerTax.isPending}
                            onClick={() => setCustomerTax.mutate("exempt")}
                        >
                            {resolving === "exempt" && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
                            Tax exempt
                        </Button>
                    </div>
                </div>
            )}

            {/* Per-document override (draft-only). */}
            {editable && !editing && (
                <button
                    type="button"
                    className="text-xs text-primary underline underline-offset-2"
                    onClick={() => setEditing(true)}
                >
                    {overridden ? `Edit tax override for this ${docType}` : `Override tax for this ${docType} only`}
                </button>
            )}
            {editable && editing && (
                <div className="space-y-2 rounded-md border p-2">
                    <Select value={choice} onChange={(e) => setChoice(e.target.value)}>
                        <option value="inherit">
                            Inherit from customer ({customerStatus === "unknown" ? "not set" : customerStatus})
                        </option>
                        <option value="taxable">Taxable — override</option>
                        <option value="exempt">Exempt — override</option>
                    </Select>
                    {choice !== "inherit" && (
                        <Input
                            placeholder="Reason for the override (required)"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                        />
                    )}
                    <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => setEditing(false)}>
                            Cancel
                        </Button>
                        <Button
                            size="sm"
                            disabled={save.isPending || (choice !== "inherit" && !reason.trim())}
                            onClick={() => save.mutate()}
                        >
                            {save.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save"}
                        </Button>
                    </div>
                </div>
            )}

            {!editable && <p className="text-[11px] text-muted-foreground">Issued — tax treatment locked.</p>}
        </div>
    );
}
